import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  AutocompleteInput,
  Create,
  DataProvider,
  DateInput,
  Labeled,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  minValue,
  required,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
} from 'react-admin';
import { useClerk, useUser } from '@clerk/clerk-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { FormSectionHeader } from 'src/client/components/forms/form-components';
import { resolveCurrentLineItemReviewer } from 'src/client/providers/extended-data-provider-methods/mutations';
import { formatCampaignTitle } from '../campaigns/CampaignList';
import { removeEmptyGeneratedNumber } from '../campaigns/CampaignCreate';
import { LineItemArchiveButton } from './LineItemArchiveButton';

type CampaignRecord = {
  id?: string;
  campaignNumber?: number | null;
  name?: string | null;
};

export const campaignNameFilterToQuery = (searchText: string) => ({
  name: { operator: 'includesInsensitive', value: searchText },
});

type ReviewerRecord = {
  id: string;
  name?: string | null;
  email?: string | null;
};

export type LineItemRecord = {
  id?: string;
  campaignId?: string | null;
  campaign?: CampaignRecord | null;
  campaignByCampaignId?: CampaignRecord | null;
  copiedFromLineItemId?: string | null;
  lineItemNumber?: number | null;
  name?: string | null;
  gamLineItemId?: string | null;
  externalPioId?: string | null;
};

const REVIEWER_HELPER_TEXT = 'Leave blank if self-reviewed';

const emptyToNull = (value: unknown) => (value === '' ? null : value);

const nullishOrBlank = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);

export const parseOptionalNumber = (value: unknown) => {
  if (nullishOrBlank(value)) return null;

  const normalized = typeof value === 'string' ? value.replace(/[$,]/g, '') : value;
  const parsed = Number(normalized);

  return Number.isNaN(parsed) ? value : parsed;
};

export const formatCurrency = (value: unknown) => {
  if (nullishOrBlank(value)) return '-';

  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/[$,]/g, ''));
  if (Number.isNaN(parsed)) return String(value);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(parsed);
};

export const formatTargetingJson = (value: unknown) => {
  if (nullishOrBlank(value)) return 'No targeting';

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  return JSON.stringify(value, null, 2);
};

export const formatLineItemTitle = (record?: LineItemRecord) => {
  if (!record) return '';
  const number = record.lineItemNumber ? `#${record.lineItemNumber}` : 'Unnumbered';
  return record.name ? `${number} ${record.name}` : number;
};

export const formatReviewerOption = (record?: Partial<ReviewerRecord>) => {
  if (!record) return '';

  const name = record.name?.trim();
  const email = record.email?.trim();

  if (name && email) return `${name} (${email})`;
  return name || email || 'Unnamed user';
};

export const findReviewerByEmail = (reviewers: ReviewerRecord[], email: string) => {
  const normalizedEmail = email.trim().toLowerCase();
  return reviewers.find(userRecord => userRecord.email?.trim().toLowerCase() === normalizedEmail);
};

type ReviewerLookupDataProvider = Pick<DataProvider, 'getList'>;

export const resolveReviewerForCurrentUser = async ({
  currentUserEmail,
  dataProvider,
  getToken,
  resolveCurrentReviewer = resolveCurrentLineItemReviewer,
}: {
  currentUserEmail: string;
  dataProvider: ReviewerLookupDataProvider;
  getToken: () => Promise<string | null>;
  resolveCurrentReviewer?: (getToken: () => Promise<string | null>) => Promise<ReviewerRecord>;
}) => {
  // Assumes the `pioUsers` provider applies no default filters; the CSAPI
  // resolver fallback only fires when this lookup returns an empty match.
  const { data } = await dataProvider.getList<ReviewerRecord>('pioUsers', {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'name', order: 'ASC' },
    filter: { email: { operator: 'includesInsensitive', value: currentUserEmail } },
  });
  const reviewer = findReviewerByEmail(data, currentUserEmail);

  return reviewer?.id ? reviewer : resolveCurrentReviewer(getToken);
};

export const lineItemStatusChoices = [
  { id: 'inactive', name: 'Inactive' },
  { id: 'ready', name: 'Ready' },
  { id: 'delivering', name: 'Delivering' },
];

export const DEFAULT_LINE_ITEM_STATUS = 'inactive';

export const getLineItemStatusChoices = (currentValue?: string | null) => {
  if (!currentValue || lineItemStatusChoices.some(choice => choice.id === currentValue)) {
    return lineItemStatusChoices;
  }

  return [{ id: currentValue, name: `Legacy: ${currentValue}` }, ...lineItemStatusChoices];
};

export const invoiceStatusChoices = [
  { id: 'in_progress', name: 'In progress' },
  { id: 'ready_for_finance', name: 'Ready for finance' },
  { id: 'on_hold', name: 'On hold' },
];

export const getInvoiceStatusChoices = (currentValue?: string | null) => {
  if (!currentValue || invoiceStatusChoices.some(choice => choice.id === currentValue)) {
    return invoiceStatusChoices;
  }

  return [{ id: currentValue, name: `Legacy: ${currentValue}` }, ...invoiceStatusChoices];
};

const stripReadOnlyAndRelationValues = (data: Record<string, any>) => {
  const {
    id,
    campaign,
    campaignByCampaignId,
    createdAt,
    externalPioId,
    lineItemByCopiedFromLineItemId,
    lineItemDeliveries,
    lineItemsByCopiedFromLineItemId,
    pioUserByReviewedBy,
    product,
    productByProductId,
    targeting,
    updatedAt,
    userByReviewedBy,
    ...input
  } = data;

  return input;
};

export const transformLineItem = (data: Record<string, any>) => {
  const input = stripReadOnlyAndRelationValues(removeEmptyGeneratedNumber(data, 'lineItemNumber'));

  return {
    ...input,
    campaignId: emptyToNull(input.campaignId),
    productId: emptyToNull(input.productId),
    copiedFromLineItemId: emptyToNull(input.copiedFromLineItemId),
    reviewedBy: emptyToNull(input.reviewedBy),
    goal: parseOptionalNumber(input.goal),
    contractedUnits: parseOptionalNumber(input.contractedUnits),
    costPerUnit: parseOptionalNumber(input.costPerUnit),
    invoiceAmount: parseOptionalNumber(input.invoiceAmount),
    invoiceUnits: parseOptionalNumber(input.invoiceUnits),
    revenue: parseOptionalNumber(input.revenue),
    revenueUnits: parseOptionalNumber(input.revenueUnits),
    gamLineItemId: emptyToNull(input.gamLineItemId),
    adServerId: emptyToNull(input.adServerId),
    archivedAt: emptyToNull(input.archivedAt),
  };
};

export const getCampaignIdFromLineItem = (record: any) =>
  record?.campaignId ?? record?.campaign?.id ?? record?.campaignByCampaignId?.id;

export const getLineItemFormDefaultValues = ({
  defaultCampaignId,
  mode,
}: {
  defaultCampaignId?: string;
  mode: 'Create' | 'Update';
}) => {
  const defaultValues: Record<string, string> = {};

  if (defaultCampaignId) defaultValues.campaignId = defaultCampaignId;
  if (mode === 'Create') defaultValues.status = DEFAULT_LINE_ITEM_STATUS;

  return Object.keys(defaultValues).length ? defaultValues : undefined;
};

export const getNextLineItemNumber = (records: Pick<LineItemRecord, 'lineItemNumber'>[]) =>
  records.reduce((max, record) => {
    const lineItemNumber = Number(record.lineItemNumber);
    return Number.isFinite(lineItemNumber) ? Math.max(max, lineItemNumber) : max;
  }, 0) + 1;

type LineItemNumberRecord = {
  id: string | number;
  lineItemNumber?: number | null;
};

const DuplicateCampaignLineItemNumberSync = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const record = useRecordContext<LineItemRecord>();
  const { getValues, setValue } = useFormContext();
  const watchedCampaignId = useWatch({ name: 'campaignId' });
  const requestVersionRef = useRef(0);
  const originalCampaignId = getCampaignIdFromLineItem(record);
  const targetCampaignId = typeof watchedCampaignId === 'string' ? watchedCampaignId : undefined;

  useEffect(() => {
    if (!record?.id || !record.copiedFromLineItemId || !targetCampaignId || targetCampaignId === originalCampaignId) {
      return;
    }

    const requestVersion = ++requestVersionRef.current;
    const lineItemNumberAtRequestStart = getValues('lineItemNumber');

    dataProvider
      .getList<LineItemNumberRecord>('lineItems', {
        filter: { campaignId: targetCampaignId, includeArchived: true },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'lineItemNumber', order: 'DESC' },
      })
      .then(({ data }) => {
        if (
          requestVersion !== requestVersionRef.current ||
          getValues('campaignId') !== targetCampaignId ||
          getValues('lineItemNumber') !== lineItemNumberAtRequestStart
        ) {
          return;
        }

        setValue('lineItemNumber', getNextLineItemNumber(data), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      })
      .catch(() => {
        notify('Could not set the next line-item number for the selected campaign', { type: 'warning' });
      });
  }, [
    dataProvider,
    getValues,
    notify,
    originalCampaignId,
    record?.copiedFromLineItemId,
    record?.id,
    setValue,
    targetCampaignId,
  ]);

  return null;
};

const LineItemFormToolbar = ({
  defaultCampaignId,
  mode,
}: {
  defaultCampaignId?: string;
  mode: 'Create' | 'Update';
}) => {
  const redirect = useRedirect();
  const record = useRecordContext<LineItemRecord>();
  const campaignId = getCampaignIdFromLineItem(record) ?? defaultCampaignId;

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Button
        variant="text"
        color="inherit"
        startIcon={<CloseIcon />}
        onClick={() => (campaignId ? redirect('show', 'campaigns', campaignId) : redirect('list', 'lineItems'))}
      >
        Cancel
      </Button>
      <Box display="flex" gap={1}>
        <SaveButton label="Save line item" />
        {mode === 'Update' ? <LineItemArchiveButton /> : null}
      </Box>
    </Toolbar>
  );
};

const FormSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box component="section" width="100%">
    <FormSectionHeader>{title}</FormSectionHeader>
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={2}>
      {children}
    </Box>
  </Box>
);

const ReadOnlyTextField = ({ source, label }: { source: string; label: string }) => (
  <Labeled source={source} label={label}>
    <TextField source={source} emptyText="-" />
  </Labeled>
);

const CurrencyInput = ({ source, label }: { source: string; label: string }) => (
  <NumberInput
    source={source}
    label={label}
    fullWidth
    parse={parseOptionalNumber}
    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
  />
);

const GamLineItemIdInput = () => {
  const record = useRecordContext<LineItemRecord>();
  const isLocked = Boolean(record?.gamLineItemId);

  return (
    <TextInput
      source="gamLineItemId"
      label="GAM Line Item ID"
      fullWidth
      resettable={!isLocked}
      helperText={isLocked ? 'Set by GAM shell workflow' : undefined}
      InputProps={{ readOnly: isLocked }}
    />
  );
};

const CopiedFromLineItemInput = () => {
  const record = useRecordContext<LineItemRecord>();

  return (
    <ReferenceInput
      source="copiedFromLineItemId"
      reference="lineItems"
      filter={record?.id ? { id: { operator: 'notEqualTo', value: record.id } } : undefined}
      sort={{ field: 'lineItemNumber', order: 'DESC' }}
    >
      <AutocompleteInput label="Copied From Line Item" optionText={formatLineItemTitle} fullWidth />
    </ReferenceInput>
  );
};

export const getCurrentUserEmail = (user: ReturnType<typeof useUser>['user']) =>
  user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress;

const ReviewedByInput = () => {
  const dataProvider = useDataProvider();
  const { session } = useClerk();
  const notify = useNotify();
  const { user } = useUser();
  const { setValue } = useFormContext();
  const [isMarkingReviewer, setIsMarkingReviewer] = useState(false);
  const currentUserEmail = getCurrentUserEmail(user);

  const handleMarkCurrentUser = async () => {
    if (!currentUserEmail) {
      notify('Current user email is unavailable', { type: 'warning' });
      return;
    }

    setIsMarkingReviewer(true);

    try {
      const reviewer = await resolveReviewerForCurrentUser({
        currentUserEmail,
        dataProvider,
        getToken: () => session?.getToken() ?? Promise.resolve(null),
      });

      if (!reviewer?.id) {
        notify('Current user could not be resolved as a reviewer', { type: 'warning' });
        return;
      }

      setValue('reviewedBy', reviewer.id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      notify('Marked current user as reviewer');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not mark current user as reviewer', { type: 'warning' });
    } finally {
      setIsMarkingReviewer(false);
    }
  };

  return (
    <Box
      display="flex"
      gap={1}
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
    >
      <Box flex="1 1 360px" minWidth={0}>
        <ReferenceInput source="reviewedBy" reference="pioUsers" sort={{ field: 'name', order: 'ASC' }}>
          <AutocompleteInput
            label="Reviewed By"
            optionText={formatReviewerOption}
            fullWidth
            helperText={REVIEWER_HELPER_TEXT}
            filterToQuery={searchText => ({
              name: { operator: 'includesInsensitive', value: searchText },
            })}
            shouldRenderSuggestions={() => true}
          />
        </ReferenceInput>
      </Box>
      <Button
        variant="outlined"
        startIcon={<PersonIcon />}
        onClick={handleMarkCurrentUser}
        disabled={isMarkingReviewer || !currentUserEmail}
        sx={{ flexShrink: 0, mt: { sm: 1 } }}
      >
        Mark me as reviewer
      </Button>
    </Box>
  );
};

const TargetingJsonViewer = () => {
  const record = useRecordContext<Record<string, any>>();

  return (
    <Box
      component="details"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        width: '100%',
      }}
    >
      <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>
        Targeting JSON
      </Box>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1,
          mt: 2,
          overflow: 'auto',
          p: 2,
          whiteSpace: 'pre-wrap',
        }}
      >
        {formatTargetingJson(record?.targeting)}
      </Box>
    </Box>
  );
};

const StatusInput = () => {
  const record = useRecordContext<Record<string, any>>();

  return (
    <SelectInput source="status" label="Status" choices={getLineItemStatusChoices(record?.status)} fullWidth />
  );
};

const InvoiceStatusInput = () => {
  const record = useRecordContext<Record<string, any>>();

  return (
    <SelectInput
      source="invoiceStatus"
      label="Invoice Status"
      choices={getInvoiceStatusChoices(record?.invoiceStatus)}
      fullWidth
      emptyText="Unset"
    />
  );
};

export const LineItemForm = ({
  defaultCampaignId,
  mode,
}: {
  defaultCampaignId?: string;
  mode: 'Create' | 'Update';
}) => (
  <SimpleForm
    defaultValues={getLineItemFormDefaultValues({ defaultCampaignId, mode })}
    toolbar={<LineItemFormToolbar defaultCampaignId={defaultCampaignId} mode={mode} />}
    mode="onBlur"
    reValidateMode="onBlur"
    sx={{ maxWidth: 1040 }}
  >
    <FormSection title="Identity">
      <ReferenceInput source="campaignId" reference="campaigns" sort={{ field: 'campaignNumber', order: 'DESC' }}>
        <AutocompleteInput
          label="Campaign"
          optionText={(record?: CampaignRecord) => formatCampaignTitle(record)}
          fullWidth
          validate={required()}
          disabled={Boolean(defaultCampaignId)}
          filterToQuery={campaignNameFilterToQuery}
        />
      </ReferenceInput>
      <DuplicateCampaignLineItemNumberSync />
      <TextInput source="name" label="Line Item Name" fullWidth resettable validate={required()} />

      <ReferenceInput source="productId" reference="products" sort={{ field: 'name', order: 'ASC' }}>
        <AutocompleteInput
          label="Product"
          optionText="name"
          fullWidth
          filterToQuery={searchText => ({ name: { operator: 'includesInsensitive', value: searchText } })}
          shouldRenderSuggestions={() => true}
        />
      </ReferenceInput>
      <NumberInput
        source="lineItemNumber"
        label="Line Item Number"
        fullWidth
        min={1}
        validate={minValue(1)}
        helperText={
          mode === 'Create' ? 'Leave blank for the next number' : 'Leave unchanged to keep the current number'
        }
      />
    </FormSection>

    <FormSection title="Schedule">
      <DateInput source="startDate" label="Start Date" fullWidth validate={required()} />
      <DateInput source="endDate" label="End Date" fullWidth validate={required()} />
      <StatusInput />
      <TextInput source="priority" label="Priority" fullWidth resettable />
      <TextInput source="itemType" label="Item Type" fullWidth resettable />
      <TextInput source="deliveryRateType" label="Delivery Rate Type" fullWidth resettable />
    </FormSection>

    <FormSection title="Pricing">
      <NumberInput source="goal" label="Goal" fullWidth parse={parseOptionalNumber} />
      <TextInput source="goalType" label="Goal Type" fullWidth resettable />
      <NumberInput source="contractedUnits" label="Contracted Units" fullWidth parse={parseOptionalNumber} />
      <TextInput source="costBasis" label="Cost Basis" fullWidth resettable />
      <NumberInput source="costPerUnit" label="Cost Per Unit" fullWidth parse={parseOptionalNumber} />
      <TextInput source="currencyCode" label="Currency Code" fullWidth resettable />
    </FormSection>

    <FormSection title="Custom">
      <CurrencyInput source="invoiceAmount" label="Invoice Amount" />
      <NumberInput source="invoiceUnits" label="Invoice Units" fullWidth parse={parseOptionalNumber} />
      <InvoiceStatusInput />
      <CurrencyInput source="revenue" label="Revenue" />
      <NumberInput source="revenueUnits" label="Revenue Units" fullWidth parse={parseOptionalNumber} />
      <TextInput source="specialty" label="Specialty" fullWidth resettable />
    </FormSection>

    <Box component="section" width="100%">
      <FormSectionHeader>Targeting</FormSectionHeader>
      <TargetingJsonViewer />
    </Box>

    <FormSection title="Ad server">
      <TextInput source="adServer" label="Ad Server" fullWidth resettable />
      <TextInput source="adServerId" label="Ad Server ID" fullWidth resettable />
      <GamLineItemIdInput />
    </FormSection>

    <FormSection title="System metadata">
      <ReadOnlyTextField source="externalPioId" label="External PIO ID" />
      <CopiedFromLineItemInput />
      <ReviewedByInput />
    </FormSection>

    <TextInput source="description" label="Description" fullWidth resettable multiline minRows={3} />
  </SimpleForm>
);

export const LineItemCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const location = useLocation();
  const campaignId = new URLSearchParams(location.search).get('campaignId') ?? undefined;

  return (
    <Create
      resource="lineItems"
      transform={(data: any) =>
        transformLineItem({
          ...data,
          campaignId: data.campaignId ?? campaignId,
          status: nullishOrBlank(data.status) ? DEFAULT_LINE_ITEM_STATUS : data.status,
        })
      }
      mutationOptions={{
        onSuccess: (data: any) => {
          notify('Saved line item');
          const redirectCampaignId = getCampaignIdFromLineItem(data);
          redirectCampaignId ? redirect('show', 'campaigns', redirectCampaignId) : redirect('list', 'lineItems');
        },
      }}
    >
      <LineItemForm defaultCampaignId={campaignId} mode="Create" />
    </Create>
  );
};
